import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addProsecutorFeedback,
  editReceivingInvestigationFile2,
} from "services/prosecutorPoliceService";
import { InvestigationFile, ProsecutorDecisionFeedback, Roles } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  file: InvestigationFile;
  showFeedbacks?: boolean;
  bare?: boolean;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const ProsecutorFeedback: FC<Props> = ({
  file,
  setRefetch,
  showFeedbacks = true,
  bare = false,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>("");
  const [show] = useState(true);
  const isCoordinator = hasRole(loginData?.roles, [
    Roles.coordinator,
    Roles.corruptionCoordinator,
    Roles.economicCoordinator,
    Roles.variousCoordinator,
  ]);

  const submitFeedback = () => {
    if (!result.trim()) return;
    setLoading(true);
    addProsecutorFeedback({
      investigation_file: file.id ?? 0,
      feedback_text: result,
      uu_id: 0,
    })
      .then(() => {
        editReceivingInvestigationFile2({
          id: file.id ?? 0,
          prosecutor_decision_status: "revised",
        });
        setRefetch(true);
        setLoading(false);
        setResult("");
        showTooltip(t("successfully_submitted"), "success");
      })
      .catch((error) => {
        setLoading(false);
        showTooltip(
          error?.response?.data?.detail ||
            errorHandlerOnForm(error) ||
            errorHandler(error) ||
            t("something_went_wrong_please_try_again"),
          "error",
        );
      });
  };

  const content = (
    <>
      {showFeedbacks && (
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-semibold text-red-600 cursor-pointer">
            {t("prosecutor_feedback", { defaultValue: "Prosecutor Feedback" })}
          </h3>
          <hr className="flex-1 border-t border-gray-200 ml-4" />
        </div>
      )}

      <div className={show ? "block" : "hidden"}>
          {file?.prosecutor_feedbacks &&
            file?.prosecutor_feedbacks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No feedback found.
              </div>
            )}
          {showFeedbacks &&
            file?.prosecutor_feedbacks &&
            file?.prosecutor_feedbacks?.map(
              (feedback: ProsecutorDecisionFeedback, index: number) => (
                <div key={index} className="mb-4">
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                    <p className="text-gray-900 font-medium">
                      {feedback?.feedback_text}
                    </p>
                  </div>
                </div>
              )
            )}

          {isCoordinator && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">↩️</span>
                <label
                  htmlFor="prosecutor-feedback"
                  className="text-sm font-semibold text-gray-900"
                >
                  {t("respond_back_to_prosecutor")}
                </label>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                {t("respond_back_hint", {
                  defaultValue:
                    "Explain what the prosecutor should revise. This sends the decision back for revision.",
                })}
              </p>
              <textarea
                id="prosecutor-feedback"
                placeholder={t("write_your_feedback_here", {
                  defaultValue: "Write your feedback here…",
                })}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-y"
                name="comment"
                value={result}
                rows={4}
                onChange={(e) => setResult(e.target.value)}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-400">
                  {result.trim().length} {t("characters", {
                    defaultValue: "characters",
                  })}
                </span>
                <button
                  type="button"
                  className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg font-medium text-sm text-white bg-red-600 hover:bg-red-700 transition-colors ${
                    loading || !result.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  disabled={loading || !result.trim()}
                  onClick={submitFeedback}
                >
                  {loading ? (
                    <>
                      {LoadingIcon}
                      {t("submitting")}
                    </>
                  ) : (
                    <>↩️ {t("submit_response")}</>
                  )}
                </button>
              </div>
            </div>
          )}
      </div>
    </>
  );

  if (bare) return content;

  return (
    <div className="mt-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {content}
      </div>
    </div>
  );
};

export default ProsecutorFeedback;
